import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import swal from "../services/swalConfig";
import { login } from "../api/handleLoginApi";
import { logout } from "../api/handleLogoutApi";
import { userProfile } from "../api/userProfileApi";
import { googleAuth } from "../api/googleAuthApi";
import { registerCompany } from "../api/CompanyApi";
import { checkSession } from "../api/AuthApi";
import { syncLanguageWithUser } from "../i18n/LanguageService";
import i18n from "../i18n";

const GlobalContext = createContext({});

export const GlobalContextProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    let userProfile = localStorage.getItem("userProfile");

    if (userProfile) {
      return JSON.parse(userProfile);
    }

    return null;
  });

  const [isLoading, setLoading] = useState(false);
  // false selama sesi SSO sedang dicek (hanya terjadi bila localStorage belum punya userProfile).
  const [authChecked, setAuthChecked] = useState(() =>
    Boolean(localStorage.getItem("userProfile")),
  );
  const navigate = useNavigate();

  const syncUserState = (nextUser) => {
    setUser(nextUser);

    if (nextUser) {
      localStorage.setItem("userProfile", JSON.stringify(nextUser));
      return;
    }

    localStorage.removeItem("userProfile");
  };

  const updateActiveWorkspace = (workspacePatch) => {
    setUser((prevUser) => {
      if (!prevUser?.workspace) {
        return prevUser;
      }

      const nextUser = {
        ...prevUser,
        workspace: {
          ...prevUser.workspace,
          ...workspacePatch,
        },
      };

      localStorage.setItem("userProfile", JSON.stringify(nextUser));
      return nextUser;
    });
  };

  // Clean up pending registration data if user is already logged in with valid workspace
  useEffect(() => {
    if (user && user.workspace && user.workspace.company_id) {
      const pendingData = localStorage.getItem("pendingRegisterCompany");
      if (pendingData) {
        console.log("Cleaning up stale pending registration data");
        localStorage.removeItem("pendingRegisterCompany");
      }
    }
  }, [user]);

  // SSO: user yang login di sewoork.com punya cookie sesi yang valid tapi localStorage
  // console masih kosong — pulihkan profilnya tanpa perlu login ulang.
  useEffect(() => {
    if (authChecked) {
      return;
    }

    let cancelled = false;

    const restoreSession = async () => {
      try {
        const session = await checkSession();

        if (session?.authenticated) {
          const dataUser = await userProfile();

          if (!cancelled && dataUser?.data) {
            syncUserState(dataUser.data);
          }
        }
      } catch (error) {
        // Gagal cek sesi = anggap belum login; halaman login tetap tampil.
      } finally {
        if (!cancelled) {
          setAuthChecked(true);
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  // Bahasa akun di server (users.language) ↔ pilihan bahasa di browser ini.
  useEffect(() => {
    if (user?.user_id) {
      syncLanguageWithUser(user);
    }
  }, [user?.user_id, user?.language]);

  // Also clean up on mount if user is already set
  useEffect(() => {
    const pendingData = localStorage.getItem("pendingRegisterCompany");
    if (pendingData && user && user.workspace) {
      localStorage.removeItem("pendingRegisterCompany");
    }
  }, []);

  const processPendingRegister = async () => {
    const pendingData = localStorage.getItem("pendingRegisterCompany");

    if (!pendingData) {
      return true;
    }

    const payload = JSON.parse(pendingData);

    try {
      const response = await registerCompany(payload);

      if (response?.status === 200) {
        localStorage.removeItem("pendingRegisterCompany");

        // Refresh user profile to get updated workspace
        const updatedUserData = await userProfile();
        if (updatedUserData) {
          syncUserState(updatedUserData.data);
        }

        swal.fire({
          title: i18n.t("company_registration.success_title"),
          text: i18n.t("company_registration.success_text"),
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        return true;
      }

      swal.fire({
        title: i18n.t("company_registration.failed_title"),
        text: response?.message || i18n.t("company_registration.failed_text"),
        icon: "error",
        showConfirmButton: true,
      });
      return false;
    } catch (error) {
      swal.fire({
        title: i18n.t("company_registration.failed_title"),
        text: i18n.t("company_registration.failed_text"),
        icon: "error",
        showConfirmButton: true,
      });
      return false;
    }
  };

  const redirectAfterAuth = (profile) => {
    const hasConsoleAccess = profile?.console_access !== false;

    if (hasConsoleAccess) {
      navigate("/dashboard");
      return;
    }

    navigate("/onboarding");
  };

  const loginApiCall = async (username, password) => {
    try {
      const loginResponse = await login(username, password);

      if (loginResponse.status === 200) {
        const dataUser = await userProfile();

        if (dataUser) {
          syncUserState(dataUser.data);

          const pendingSuccess = await processPendingRegister();
          if (pendingSuccess) {
            redirectAfterAuth(dataUser.data);
          } else {
            navigate("/register");
          }
        }
      }

      return loginResponse;
    } catch (error) {
      return error;
    }
  };

  const googleLoginApiCall = async (payload) => {
    setLoading(true);

    try {
      await googleAuth(payload);

      const dataUser = await userProfile();

      syncUserState(dataUser.data);

      const pendingSuccess = await processPendingRegister();
      if (pendingSuccess) {
        redirectAfterAuth(dataUser.data);
      } else {
        navigate("/register");
      }
    } catch (error) {
      console.log("Error when try login" + error);
    } finally {
      setLoading(false);
    }
  };

  const logoutApiCall = async () => {
    await logout();

    localStorage.removeItem("pendingRegisterCompany"); // Clean up any stale pending data
    syncUserState(null);
    navigate("/login");
  };

  const ChangeWorkspace = (payload) => {
    updateActiveWorkspace(payload);
  };

  return (
    <GlobalContext.Provider
      value={{
        loginApiCall,
        googleLoginApiCall,
        logoutApiCall,
        ChangeWorkspace,
        syncUserState,
        authChecked,
        updateActiveWorkspace,
        user,
        setUser,
        setLoading,
        isLoading,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export default GlobalContext;
